import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, Checkbox, FormControlLabel, Modal, Button } from '@mui/material';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import { ToWords } from 'to-words';
import { toast } from 'react-toastify';
import InvoiceTaxEdit from 'components/invoiceTaxEdit/InvoiceTaxEdit';
import { getReservationDetailsObject, formatCurrency, formatNumber } from 'utils/utils';
import { generateInvoice, getBillingInvoiceDetails } from 'redux/actions/BillingAction';
// import { fetchAdminUser } from "redux/actions/AdminUserAction";

import CommonButton from 'components/commonButton/CommonButton';
import CommonNoDataFound from 'components/CommonNoDataFound/CommonNoDataFound';
import Loader from 'components/loader/Loader';
import { tripPerformaInvoiceData } from './BillingInvoiceData';
import { tripDutyTypeEnum } from 'utils/enumObj';
import consolidatedInvoiceService from 'services/ConsolidatedInvoiceService';
import NotifyRecipientsModal from 'components/notifyRecipientsModal/NotifyRecipientsModal';

// Helper function to get toll and parking charges
const getTollAndParkingCharges = (tripDetails) => {
  const tollCharges =
    tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'tollCharges',
    )?.addedCharge || 0;
  const parkingCharges =
    tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'parkingCharge',
    )?.addedCharge || 0;

  if (tollCharges || parkingCharges) {
    return parseFloat(tollCharges) + parseFloat(parkingCharges);
  }

  return parseFloat(tripDetails?.tollAndParkingCharges || 0);
};

// Helper function to get garage charges
const getGarageCharges = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const garageCharge = breakdown.find((item) => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];

  // totalCharge is the money; `rate` is the ₹/km tariff.
  const garageToPickup = parseFloat(garageBreakup[0]?.totalCharge || 0);
  const dropToGarage = parseFloat(garageBreakup[1]?.totalCharge || 0);

  return garageToPickup + dropToGarage;
};

// Helper function to calculate Sub Total A (base fare + extra charges + surcharges + platform charge)
const calculateSubTotalA = (tripDetails) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};

  const breakdown = tripPricing?.breakdown || [];
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  // Overage charges live in the pricing-engine breakdown; the legacy
  // top-level fields only exist on the old pricing path.
  const extraKmFromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrakm',
  )?.addedCharge;
  const extraKmCharges =
    extraKmFromBreakdown !== undefined && extraKmFromBreakdown !== null
      ? parseFloat(extraKmFromBreakdown) || 0
      : tripDetails?.type === tripDutyTypeEnum?.rental
        ? parseFloat(tripDetails?.extraKmCharges || 0)
        : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrFromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrahrs',
  )?.addedCharge;
  const extraHrCharges =
    extraHrFromBreakdown !== undefined && extraHrFromBreakdown !== null
      ? parseFloat(extraHrFromBreakdown) || 0
      : parseFloat(tripDetails?.extraHrCharges || 0);
  const garageCharges = getGarageCharges(tripDetails);
  const baseFare = parseFloat(tripPricing?.baseFareDisplay || 0);

  return (
    baseFare +
    baseRate +
    surCharge +
    platformCharge +
    extraKmCharges +
    extraHrCharges +
    garageCharges
  );
};

// Helper function to calculate Sub Total B (all additional charges)
const calculateSubTotalB = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];

  const tollAndParking = getTollAndParkingCharges(tripDetails);
  const driverAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'driverAllowance')?.addedCharge || 0,
  );
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'nightAllowance')?.addedCharge || 0,
  );
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'placard')?.addedCharge || 0,
  );
  const additionalCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'additionalCharges')?.addedCharge || 0,
  );

  return (
    tollAndParking +
    driverAllowance +
    handlingCharges +
    nightAllowance +
    fuelSurcharge +
    interstateTax +
    placardCharge +
    additionalCharges
  );
};

const BillingPerformaInvoice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toWords = new ToWords();
  const pathName = location?.pathname?.split('/');
  const [searchParams] = useSearchParams();
  const [consolidatedInvoiceLoading, setConsolidatedInvoiceLoading] = useState(false);

  // const [sendEmailOpen, setSendEmailOpen] = useState(false);
  // const [selectedRecipients, setSelectedRecipients] = useState([]);

  const params = Object.fromEntries([...searchParams]);

  const queryParams = {
    cityId: params?.cityId || '',
    cityName: params?.cityName || '',
    companyId: params?.companyId || '',
    companyName: params?.companyName || '',
    tripId: params?.tripId || '',
    page: params?.page || '',
    pageRowSize: params?.pageRowSize || '',
    status: params?.status || '',
    fromDate: params?.startDate === 'undefined' ? '' : params?.startDate ? params?.startDate : '',
    toDate: params?.endDate === 'undefined' ? '' : params?.endDate ? params?.endDate : '',
    magicToken: params?.magicToken || '',
    pageType: params?.pageType || '',
  };

  // useEffect(() => {
  //   if (pathName?.includes("operation")) {
  //     dispatch(getBillingInvoiceDetails(queryParams?.tripId, "private"));
  //   } else {
  //     dispatch(getBillingInvoiceDetails(queryParams?.magicToken, "public"));
  //   }
  // }, []);

  useEffect(() => {
    if (pathName?.includes('operation')) {
      dispatch(getBillingInvoiceDetails(queryParams?.tripId, 'private'));
      // dispatch(fetchAdminUser());
    } else {
      dispatch(getBillingInvoiceDetails(queryParams?.magicToken, 'public'));
    }
  }, []);

  const [isEditingTax, setIsEditingTax] = useState(false);

  const tripDetails = useSelector(
    (state) => state.getBillingInvoiceDetails?.bilingInvoiceDetails?.data,
  );
  const showStart = tripDetails?.additionalInfo?.startOtpRequired;
  const showEnd = tripDetails?.additionalInfo?.endOtpRequired;
  const bothShown = showStart && showEnd;
  // const adminUsers = useSelector((state) => state.getAdminUser?.adminUser);

  // console.log("tripDetails", tripDetails);

  const tripDetailsLoader = useSelector((state) => state.getBillingInvoiceDetails?.loading);
  const tripTypeDetails = getReservationDetailsObject(tripDetails);
  const tripExtraCharges =
    tripDetails?.type === tripDutyTypeEnum?.rental
      ? tripDetails?.extraKmCharges
      : tripDetails?.extraPrice;

  const subTotalA = calculateSubTotalA(tripDetails);
  const subTotalB = calculateSubTotalB(tripDetails);
  const totalAB = Number(subTotalA) + Number(subTotalB);
  const totalGstAmount =
    totalAB *
    0.01 *
    (Number(tripDetails?.Payments?.[0]?.igst) +
      Number(tripDetails?.Payments?.[0]?.cgst) +
      Number(tripDetails?.Payments?.[0]?.sgst));
  const grossTotal = (totalAB + totalGstAmount).toFixed(2);
  const tripPerformaInvoiceDetails = tripPerformaInvoiceData(
    tripDetails,
    tripTypeDetails,
    subTotalA,
    subTotalB,
    totalAB,
    totalGstAmount,
    grossTotal,
  );

  const displayTotalAmount =
    tripDetails?.totalAmount ??
    tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount ??
    tripDetails?.price;

  const tollImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Toll');
  const parkingImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Parking');

  const interStateImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === 'Interstate-Tax');
  // const handleGenerateInvoice = () => {
  //   if (queryParams?.pageType === "generateInvoice") {
  //     dispatch(
  //       generateInvoice({
  //         tripId: queryParams?.tripId,
  //         generateType: "b2bPerforma",
  //         handleBack,
  //       }),
  //     );
  //   } else {
  //     window.open(tripDetails?.Invoices?.[0]?.name?.url, "_blank");
  //   }
  // };

  const [notifyModalOpen, setNotifyModalOpen] = useState(false);

  const handleGenerateInvoice = () => {
    if (queryParams?.pageType === 'generateInvoice') {
      dispatch(
        generateInvoice({
          tripId: queryParams?.tripId,
          generateType: 'b2bPerforma',
          handleBack: () => setNotifyModalOpen(true),
        }),
      );
    } else {
      window.open(tripDetails?.Invoices?.[0]?.name?.url, '_blank');
    }
  };

  const generateConsolidatedInvoice = async () => {
    setConsolidatedInvoiceLoading(true);
    try {
      const res = await consolidatedInvoiceService.generateConsolidatedInvoice(
        tripDetails?.parentTripId || tripDetails?.id,
      );
      if (res?.success && res?.data?.invoice?.url) {
        toast.success(res?.message || 'Consolidated invoice generated successfully');
        window.open(res.data.invoice.url, '_blank');
      } else {
        toast.error(res?.message || 'Failed to generate consolidated invoice');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to generate consolidated invoice');
      console.error('Consolidated Invoice Error:', error);
    } finally {
      setConsolidatedInvoiceLoading(false);
    }
  };

  // const sendInvoiceEmail = async () => {
  //   try {
  //     // console.log("tripDetails :", tripDetails);
  //     // console.log("invoiceId :", tripDetails?.id);

  //     await consolidatedInvoiceService.sendConsolidatedInvoiceEmail(
  //       tripDetails?.id,
  //       selectedRecipients,
  //     );

  //     toast.success("Invoice sent successfully");
  //     setSendEmailOpen(false);
  //   } catch (error) {
  //     toast.error(error?.message || "Failed to send invoice");
  //   }
  // };

  const commonGridRender = (arrayData, gridSize) => {
    return arrayData?.map((details, index) => (
      <Grid
        container
        item
        xs={gridSize?.xs}
        sm={gridSize?.sm}
        md={gridSize?.md}
        key={index}
        className="pb20"
      >
        <Grid item xs={7} className="textleft" pl={6}>
          <Typography
            variant="body2"
            className={`"colorBlack" ${details?.bold ? 'fw600' : 'op80'}`}
          >
            {details?.title}
          </Typography>
        </Grid>
        <Grid item xs={5} className="textLeft">
          <Typography
            variant="body2"
            className={`"colorBlack pr10 pl10" ${details?.bold ? 'fw600' : 'fw400'}`}
          >
            {details?.value}
          </Typography>
        </Grid>
      </Grid>
    ));
  };

  const btnText = () => {
    if (queryParams?.pageType === 'generateInvoice') {
      return 'Confirm';
    }
    return 'Download';
  };

  const handleBack = () => {
    if (queryParams?.pageType) {
      navigate(
        `/operation/allrides/bookingdetails?id=${queryParams?.tripId}&startDate=${queryParams?.fromDate}&endDate=${queryParams?.toDate}&type=allrides&tab=completed&page=${queryParams?.page}&rowPerPage=${queryParams?.pageRowSize}&search=${queryParams?.search || ''}`,
      );
    } else {
      navigate(
        `/operation/billing/billToCompany?companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&startDate=${queryParams?.fromDate}&endDate=${queryParams?.toDate}`,
      );
    }
  };

  return (
    <Grid container className="pt20 br20" justifyContent={'center'}>
      {tripDetailsLoader ? (
        <Loader />
      ) : (
        <Grid item md={11}>
          <Box className="alignStart">
            {pathName?.includes('operation') && (
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey mb20"
                role="presentation"
                onClick={handleBack}
              />
            )}
          </Box>

          <Grid container justifyContent={'space-between'} alignItems={'center'}>
            <Grid />
            {pathName?.includes('operation') && (
              <Grid container gap={2} alignItems="center" mb={1} justifyContent="end">
                <CommonButton
                  name="Send Invoice"
                  btnClass="btnMedium btnSecondary btnYellow"
                  // onClick={() => setSendEmailOpen(true)}
                  onClick={() => setNotifyModalOpen(true)}
                />

                {queryParams?.pageType === 'generateInvoice' && (
                  <CommonButton
                    name="Edit Tax"
                    btnClass="btnMedium btnSecondary btnYellow"
                    onClick={() => setIsEditingTax(true)}
                  />
                )}
                <CommonButton
                  name={btnText()}
                  iconType="download"
                  iconPlace={queryParams?.pageType !== 'generateInvoice' && 'back'}
                  btnClass="btnMedium btnSecondary btnYellow"
                  onClick={handleGenerateInvoice}
                  disabled={consolidatedInvoiceLoading}
                />
                {(tripDetails?.parentTripId ||
                  tripDetails?.additionalInfo?.tripExtendedAsNewTrip) && (
                  <CommonButton
                    name="Generate Consolidated Invoice"
                    iconType="download"
                    iconPlace="back"
                    btnClass="btnMedium btnSecondary btnYellow"
                    onClick={generateConsolidatedInvoice}
                    disabled={consolidatedInvoiceLoading}
                  />
                )}
              </Grid>
            )}
          </Grid>

          <Grid className="invoiceBorder br20 mb40">
            <Grid container className="solidBorderBottomBlack pt20" item xs={12}>
              <Grid item xs={12} pl={6}>
                <Grid md={4}>
                  <Typography
                    variant="head5"
                    component={'p'}
                    className="colorSolidBlack fw500 pb10"
                  >
                    {tripDetails?.DriverProfile?.vendorName || tripDetails?.vendorName || 'NA'}
                  </Typography>
                  <Typography variant="body2" component={'p'} className="colorBlack pb20">
                    {tripDetails?.vendorAddress || 'NA'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item xs={12} md={6}>
                {tripPerformaInvoiceDetails?.companyGstDetails1?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb20">
                    <Grid item xs={6} lg={5} xl={4} className="textleft" pl={6}>
                      <Typography variant="body2" className="colorBlack op80">
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body2">{details?.value}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={6}
                alignItems={'flex-start'}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.companyDetails2?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb20">
                    <Grid item xs={6} lg={5} xl={4} className="textleft" pl={6}>
                      <Typography variant="body2" className="colorBlack op80">
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body2">{details?.value}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt20" item xs={12}>
              <Grid container item xs={12} md={6}>
                {tripPerformaInvoiceDetails?.detailsArray1?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb20">
                    <Grid item xs={6} lg={5} xl={4} className="textleft" pl={6}>
                      <Typography variant="body2" className="colorBlack op80">
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body2">{details?.value}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={6}
                alignItems={'flex-start'}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.detailsArray2?.map((details, index) => (
                  <Grid container item xs={12} key={index} className="pb20">
                    <Grid item xs={6} lg={5} xl={4} className="textleft" pl={6}>
                      <Typography variant="body2" className="colorBlack op80">
                        {details?.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} lg={7} xl={8} className="colorBlack textLeft">
                      <Typography variant="body2">{details?.value}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt20" item xs={12}>
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray3, {
                  xs: 12,
                  sm: 6,
                  md: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt20" item xs={12}>
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4, {
                  xs: 12,
                  sm: 6,
                  md: 4,
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt20 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4Summary, {
                  xs: 12,
                  sm: 6,
                  md: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="solidBorderBottomBlack pt20" item xs={12}>
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5, {
                  xs: 12,
                  sm: 6,
                  md: 4,
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt20 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5Summary, {
                  xs: 12,
                  sm: 6,
                  md: 4,
                })}
              </Grid>
            </Grid>

            <Grid container className="pb20" item xs={12}>
              <Grid item xs={12} md={8} className="pt20">
                <Typography variant="body1" className="fw500 pb20 colorSolidBlack" pl={6}>
                  All Cheques/Draft to be drawn in favor of{' '}
                  {tripDetails?.DriverProfile?.vendorName || 'NA'}
                </Typography>
                {tripPerformaInvoiceDetails?.detailsArray6 &&
                  tripPerformaInvoiceDetails?.detailsArray6?.map((routingData, index) => (
                    <Grid container className="pb10" pl={6}>
                      <Typography variant="body3" className="colorSolidBlack">
                        {index + 1}.&nbsp;{routingData}
                      </Typography>
                    </Grid>
                  ))}
                <Grid item pt={3} pl={6}>
                  <Typography variant="body3" className="colorSolidBlack fw600">
                    Net Payable In Words:{' '}
                    {!isNaN(displayTotalAmount)
                      ? toWords
                          .convert(Number(displayTotalAmount), {
                            currency: true,
                          })
                          .toUpperCase()
                      : 'Zero'}
                  </Typography>
                </Grid>
              </Grid>
              {/* Guest Signature Section */}
              {tripDetails?.additionalInfo?.guestSignature && (
                <Grid
                  container
                  className="solidBorderTopBlack solidBorderBottomBlack pt20 pb20"
                  item
                  xs={12}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                    pl={6}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" className="colorBlack op80">
                      Guest Signature
                    </Typography>
                    <img
                      src={tripDetails?.additionalInfo?.guestSignature}
                      alt="Guest Signature"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '80px',
                        objectFit: 'contain',
                        marginLeft: '16px',
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Company Seal & Signature */}
              {tripDetails?.tripCompany?.additionalInfo?.sealSignatureConfig?.enabled && (
                <Grid
                  className="solidBorderTopBlack pt20 pb20"
                  container
                  item
                  xs={12}
                  sx={{ minHeight: '80px', alignItems: 'center' }}
                >
                  <Grid
                    item
                    xs={12}
                    pl={6}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="body2" className="colorBlack op80" sx={{ flexShrink: 0 }}>
                      Seal & Signature
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Box 1: Seal */}
                      <Box
                        sx={{
                          width: 110,
                          height: 60,
                          border: '1px solid #d0d0d0',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                        }}
                      >
                        {tripDetails?.tripCompany?.additionalInfo?.sealSignatureConfig?.sealUrl && (
                          <img
                            src={
                              tripDetails?.tripCompany?.additionalInfo?.sealSignatureConfig?.sealUrl
                            }
                            alt="Seal"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                            style={{
                              maxWidth: '90%',
                              maxHeight: '90%',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                      </Box>

                      {/* Box 2: Signature */}
                      <Box
                        sx={{
                          width: 150,
                          height: 60,
                          border: '1px solid #d0d0d0',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                        }}
                      >
                        {tripDetails?.tripCompany?.additionalInfo?.sealSignatureConfig
                          ?.signatureUrl && (
                          <img
                            src={
                              tripDetails?.tripCompany?.additionalInfo?.sealSignatureConfig
                                ?.signatureUrl
                            }
                            alt="Signature"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                            style={{
                              maxWidth: '90%',
                              maxHeight: '90%',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Tax row + Edit Tax modal — spans full width below */}
              <Grid item xs={12} className="pt20" px={6}>
                <InvoiceTaxEdit
                  tripId={queryParams?.tripId}
                  type={pathName?.includes('operation') ? 'private' : 'public'}
                  magicToken={queryParams?.magicToken || ''}
                  open={isEditingTax}
                  onClose={() => setIsEditingTax(false)}
                  showEditButton={false}
                  onSaveSuccess={() => {
                    if (pathName?.includes('operation')) {
                      dispatch(getBillingInvoiceDetails(queryParams?.tripId, 'private'));
                    } else {
                      dispatch(getBillingInvoiceDetails(queryParams?.magicToken, 'public'));
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container className="mb40">
            <Grid item xs={12} sm={6} className="height400 bgGrey justifyCenter">
              {tripDetails?.tripRouteImage?.url ? (
                <img
                  src={tripDetails?.tripRouteImage?.url}
                  alt="GoogleImage"
                  className="width100 height100"
                />
              ) : (
                <CommonNoDataFound />
              )}
            </Grid>
            <Grid item xs={12} sm={6} className="p30">
              <Typography className="fw600 pb10">Guest Start Location</Typography>
              <Typography className="pb30">
                {tripDetails?.tripStartLocation || tripDetails?.sourceAddress || ''}
              </Typography>
              <Typography className="fw600 pb10">Guest Drop Location</Typography>
              <Typography className="pb30">
                {tripDetails?.tripEndLocation || tripDetails?.destinationAddress || ''}
              </Typography>
              <Typography className="fw600 pb30">
                Distance Covered:&nbsp;&nbsp;&nbsp;
                {tripDetails?.actualDistance || 0} km
              </Typography>
              {(showStart || showEnd) && (
                <Grid container>
                  {showStart && (
                    <Grid item xs={12} sm={bothShown ? 6 : 12} className="justifyCenter">
                      <Typography className="textCenter colorPrimary fw600">
                        Start Trip OTP Verified
                        <br />
                        {tripDetails?.TripOtp?.startOtp || ''}
                      </Typography>
                    </Grid>
                  )}
                  {showEnd && (
                    <Grid item xs={12} sm={bothShown ? 6 : 12} className="justifyCenter">
                      <Typography className="textCenter colorPrimary fw600">
                        End Trip OTP Verified
                        <br />
                        {tripDetails?.TripOtp?.endOtp || ''}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* TODO: required an data from backend */}
          {tripDetails?.dutyTypeName === 'Rental' && tripDetails?.RoutingSummaries.length > 0 && (
            <Grid className="mb40">
              <Typography variant="head5" className="fw600 pb30">
                Routing Summary Form Pick-Up To Drop
              </Typography>
              <Grid container className="solidBorderBottomBlack pb20 mt20">
                <Grid xs={12} sm={1} className="justifyCenter">
                  <Typography variant="head6" className="textCenter">
                    Sr. No.
                  </Typography>
                </Grid>
                <Grid xs={12} sm={11}>
                  <Typography variant="head6">Location</Typography>
                </Grid>
              </Grid>
              {tripDetails?.RoutingSummaries &&
                tripDetails?.RoutingSummaries?.map((routingData, index) => (
                  <Grid container className="ptb20 dashedBorderBottom">
                    <Grid xs={12} sm={1} className="justifyCenter">
                      <Typography variant="body3" className="colorGrey textCenter">
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={11}>
                      <Typography variant="body3" className="colorGrey">
                        {routingData?.address}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </Grid>
          )}

          <Grid container className="mb30" gap={3} flexWrap={'nowrap'}>
            {tollImages?.[0]?.TollAndParkingReceipts &&
              tollImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={tollImages?.url}
                    alt="Toll and parking"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>

          <Grid container className="mb40" gap={3} flexWrap={'nowrap'}>
            {parkingImages?.[0]?.TollAndParkingReceipts &&
              parkingImages?.[0]?.TollAndParkingReceipts?.map((parkingImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={parkingImages?.url}
                    alt="Toll and parking"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>
          <Grid container className="mb30" gap={3} flexWrap={'nowrap'}>
            {interStateImages?.[0]?.TollAndParkingReceipts &&
              interStateImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={tollImages?.url}
                    alt="InterState-Rate"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
      )}

      {/* <Modal
        open={sendEmailOpen}
        onClose={() => setSendEmailOpen(false)}
        className="customModal"
      >
        <Box className="modalContent">
          <Typography variant="head5">Select Recipients</Typography>

          {adminUsers?.length > 0 ? (
            adminUsers.map((user) => (
              <FormControlLabel
                key={user?.id}
                control={
                  <Checkbox
                    checked={selectedRecipients.includes(user?.email)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRecipients([
                          ...selectedRecipients,
                          user?.email,
                        ]);
                      } else {
                        setSelectedRecipients(
                          selectedRecipients.filter(
                            (email) => email !== user?.email,
                          ),
                        );
                      }
                    }}
                  />
                }
                label={user?.email}
              />
            ))
          ) : (
            <Typography variant="body2">No recipients found</Typography>
          )}

          <CommonButton
            name="Send"
            btnClass="btnMedium"
            disabled={!selectedRecipients.length}
            onClick={sendInvoiceEmail}
          />
        </Box>
      </Modal> */}

      <NotifyRecipientsModal
        open={notifyModalOpen}
        toggle={() => setNotifyModalOpen(false)}
        tripId={queryParams?.tripId}
        notificationType="invoiceGeneration"
      />
    </Grid>
  );
};

export default BillingPerformaInvoice;
